import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { ordersAPI } from '../lib/api';
import { CheckCircle, XCircle, Clock, Package } from '@phosphor-icons/react';
import { logger } from '../lib/logger';
import { trackPurchase } from '../lib/analytics';

const OrderResultPage = () => {
  const { orderId } = useParams();
  const [searchParams] = useSearchParams();
  const cancelled = searchParams.get('cancelled') === 'true';
  
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrder();
    // Poll for order status updates
    const interval = setInterval(fetchOrder, 5000);
    return () => clearInterval(interval);
  }, [orderId]);

  const fetchOrder = async () => {
    try {
      const response = await ordersAPI.getOrder(orderId);
      setOrder(response.data);
    } catch (error) {
      logger.error('Failed to fetch order:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fire the GA4 + Meta Pixel purchase conversion once per order (skip cancelled page loads).
  useEffect(() => {
    if (!order || cancelled || order.status !== 'paid') return;
    const key = `conversion_fired_order_${orderId}`;
    if (sessionStorage.getItem(key)) return;
    trackPurchase(order);
    sessionStorage.setItem(key, 'true');
  }, [order, cancelled, orderId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-background py-12">
        <div className="container mx-auto px-4 text-center">
          <p className="text-text_muted mb-4">Order not found</p>
          <Link to="/" className="text-accent hover:underline">Return to home</Link>
        </div>
      </div>
    );
  }

  const getStatusConfig = () => {
    if (cancelled || order.status === 'cancelled') {
      return {
        icon: XCircle,
        color: 'text-yellow-600',
        bg: 'bg-yellow-50',
        border: 'border-yellow-200',
        title: 'Payment Cancelled',
        message: 'You cancelled the payment. Your order has not been processed.'
      };
    }
    if (order.status === 'paid') {
      return {
        icon: CheckCircle,
        color: 'text-green-600',
        bg: 'bg-green-50',
        border: 'border-green-200',
        title: 'Payment Successful!',
        message: 'Thank you for your purchase. You will receive your building plans via email shortly.'
      };
    }
    if (order.status === 'failed') {
      return {
        icon: XCircle,
        color: 'text-red-600',
        bg: 'bg-red-50',
        border: 'border-red-200',
        title: 'Payment Failed',
        message: 'Your payment could not be completed. Please try again or contact support.'
      };
    }
    return {
      icon: Clock,
      color: 'text-blue-600',
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      title: 'Payment Processing',
      message: 'Your payment is being processed. This page will update automatically.'
    };
  };

  const statusConfig = getStatusConfig();
  const StatusIcon = statusConfig.icon;

  return (
    <div className="min-h-screen bg-background py-12" data-testid="order-result-page">
      <div className="container mx-auto px-4 max-w-3xl">
        <div className={`bg-white border-2 ${statusConfig.border} p-8 mb-8`}>
          <div className="text-center">
            <StatusIcon size={64} className={`${statusConfig.color} mx-auto mb-4`} weight="duotone" />
            <h1 
              className={`text-3xl font-black ${statusConfig.color} mb-3`}
              style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}
            >
              {statusConfig.title}
            </h1>
            <p className="text-text_muted mb-6">{statusConfig.message}</p>
            
            <div className="inline-block bg-gray-100 px-6 py-3 rounded">
              <div className="text-xs text-text_muted mb-1">Order ID</div>
              <div className="font-mono font-bold text-primary" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                {order.order_id}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white border border-border p-8 mb-8">
          <h2 className="text-xl font-bold text-primary mb-6" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Order Details</h2>
          
          <div className="space-y-4">
            {order.items.map((item, index) => (
              <div key={index} className="pb-4 border-b border-border last:border-0">
                <div className="flex justify-between items-start mb-2">
                  <div className="flex-1">
                    <div className="font-bold text-primary mb-1">{item.plan_name}</div>
                    <div className="text-sm text-text_muted">
                      {item.config.file_type} • {item.config.drawing_sets.join(', ')}
                    </div>
                  </div>
                  <div className="text-accent font-bold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    R{item.total_price.toFixed(2)}
                  </div>
                </div>
              </div>
            ))}
            
            <div className="flex justify-between items-center pt-4 text-lg font-bold">
              <span>Total:</span>
              <span className="text-accent" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                R{order.total_amount.toFixed(2)}
              </span>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link
            to="/orders"
            className="inline-block bg-primary text-white px-8 py-3 rounded font-medium hover:bg-opacity-90 text-center"
            data-testid="view-orders-btn"
          >
            View My Orders
          </Link>
          <Link
            to="/plans"
            className="inline-block border-2 border-primary text-primary px-8 py-3 rounded font-medium hover:bg-gray-50 text-center"
            data-testid="continue-shopping-btn"
          >
            Continue Shopping
          </Link>
        </div>
      </div>
    </div>
  );
};

export default OrderResultPage;